import { useTheme } from "styled-components"
import Title from "../../../components/Title"
import { Container } from "./styles"
import { IListaEventoProps } from "./types"
import Input from "../../../components/Input"
import Button from "../../../components/Button"
import GroupButtons from "../../../components/GroupButtons"
import List from "../../../components/List"
import CardEvento from "../../../components/CardEvento"
import { EStatus, Evento } from "../../../models"
import Select from "../../../components/Select"
import { enumToArray } from "../../../utils/enumToArray"
import { useEffect, useState } from "react"
import Loader from "../../../components/Loader"
import * as EventoService from '../../../services/Evento/EventoService'
import { errorHandle } from "../../../utils/errorHandle"
import { toast } from "react-toastify"
import { montaQuery } from "../../../utils/montaQuery"
import { isValid } from "date-fns"
import { dataForFilter, dateForInput, inputToDate } from "../../../utils/dateFormats"
import Pagination from "../../../components/Pagination"
import { confirmAlert } from "react-confirm-alert"
import { useNavigate } from "react-router-dom"
import Form from "../../../components/Form"
import { useAuthContext } from "../../../hooks/auth"
import { removerAcentos } from "../../../utils/removerAcentos"

const ListaEvento: React.FC<IListaEventoProps> = () => {
    const [descricao, setDescricao] = useState<string>('')
    const [data, setData] = useState<Date | null>(null)
    const [ativo, setAtivo] = useState<string>('0')

    const [loading, setLoading] = useState<boolean>(false)
    const [eventos, setEventos] = useState<Evento[]>([])

    const [filtros, setFiltros] = useState<string>('')
    const [page, setPage] = useState<number>(0)
    const [totalCount, setTotalCount] = useState<number>(0)
    const rowsPerPage = 50

    const theme = useTheme()
    const navigate = useNavigate()
    const { isAdmin } = useAuthContext()

    const listaStatus = [{ id: '0', descricao: 'Todos' }, ...enumToArray(EStatus)]

    useEffect(() => {
        getEventos(filtros, page, rowsPerPage)
    }, [filtros, page, rowsPerPage])

    const getEventos = async (filtro: string, page: number, rowsPerPage: number) => {
        setLoading(true)
        await EventoService.listar(filtro, page, rowsPerPage)
            .then(res => {
                setEventos(res.data.data)
                setTotalCount(res.data.totalCount)
            })
            .catch(err => toast.error(errorHandle(err)))
            .finally(() => setLoading(false))
    }

    const handleSubmit = (): void => {
        const params = handlerFilter()
        setFiltros(params)
    }

    const limparFiltros = () => {
        setDescricao('')
        setData(null)
        setAtivo('0')
        setFiltros('')
    }

    const handlerFilter = (): string => {
        let params = ''
        if (descricao && descricao !== '') {
            params += montaQuery(params)
            params += `descricao.contains("${descricao}")`
        }
        if (isValid(data)) {
            params += montaQuery(params)
            params += `data.Date="${dataForFilter(data)}"`
        }
        if (ativo !== '0') {
            params += `&Active=${ativo === '1' ? 'true' : 'false'}`
        }
        if (params !== '') 
            setPage(0)

        return params
    }

    const handleEditar = (eventoId: string): void => {
        navigate(`/evento/${eventoId}`)
    }

    const handleListarConvidados = async (eventoId: string, descricao: string): Promise<void> => {
        navigate(`/convidado/${eventoId}/${descricao}`)
    }

    const handleRemover = async (eventoId: string, descricao: string): Promise<void> => {
        confirmAlert({
            title: 'Deletar evento',
            message: `deseja mesmo deletar o evento ${descricao}?`,
            buttons: [
                {
                    label: 'Sim',
                    onClick: async () => {
                        setLoading(true)
                        await EventoService.remover(eventoId)
                            .then(() => {
                                toast.success(`O evento ${descricao} foi deletado com sucesso`)
                                getEventos(filtros, page, rowsPerPage)
                            })
                            .catch((err) => toast.error(errorHandle(err)))
                            .finally(() => setLoading(false))
                    }
                },
                {
                    label: 'Não',
                    onClick: () => {}
                }
            ]
        })
    }

    const handleHistorico = (eventoId: string, descricao: string): void => {
        navigate(`/evento-history/${eventoId}/${descricao}`)
    }

    const handleScore = (eventoId: string, descricao: string): void => {
        navigate(`/score/${eventoId}/${descricao}`)
    }

    return(
        <>
            {loading 
                ? <Loader />
                : <Container>
                    <GroupButtons>
                        <Button 
                            $backgroundColor={theme!.colors.success}
                            type="button"
                            hidden={!isAdmin}
                            onClick={() => navigate('/usuario')}
                        >
                            Usuários
                        </Button>
                        <Button
                            $backgroundColor={theme!.colors.info}
                            type="button"
                            hidden={!isAdmin}
                            onClick={() => navigate('form-google')}
                        >
                            Form Google
                        </Button>
                        <Button
                            $backgroundColor={theme!.colors.warning}
                            type="button"
                            hidden={!isAdmin}
                            onClick={() => navigate('mensagem')}
                        >
                            Mensagem
                        </Button>
                    </GroupButtons>
                    <Title color={theme!.colors.warning} size={30}>
                        Lista de Eventos
                    </Title>
                    <Form>
                        <Input 
                            width={31}
                            widthMobile={93}
                            label="Descrição"
                            placeholder="Descrição" 
                            type="text" 
                            value={descricao} 
                            onChange={(e) => setDescricao(removerAcentos(e.target.value?.toUpperCase()))} 
                        />
                        <Input 
                            width={31}
                            widthMobile={45}
                            label="Data"
                            placeholder="Data" 
                            type="date" 
                            value={dateForInput(data)} 
                            onChange={(e) => setData(inputToDate(e.target.value))} 
                        />
                        <Select 
                            width={31}
                            widthMobile={45}
                            label="Status"
                            itens={listaStatus} 
                            value={ativo} 
                            onChange={(e) => setAtivo(e.target.value)} 
                        />
                    </Form>
                    <GroupButtons>
                        <Button 
                            $backgroundColor={theme!.colors.info} 
                            type="button" 
                            onClick={handleSubmit}
                        >
                            Buscar
                        </Button>
                        <Button 
                            $backgroundColor={theme!.colors.warning} 
                            type="button" 
                            onClick={limparFiltros}
                        >
                            Limpar
                        </Button>
                    </GroupButtons>
                    <GroupButtons>
                        <Button 
                            $backgroundColor={theme!.colors.success} 
                            type="button"
                            hidden={!isAdmin}
                            onClick={() => navigate('/evento/new')}
                        >
                            Novo
                        </Button>
                    </GroupButtons>
                    <List>
                        {
                            eventos.map(evento => (
                                <CardEvento 
                                    key={evento.id} 
                                    evento={evento} 
                                    editar={handleEditar}
                                    listarConvidados={handleListarConvidados}
                                    remover={handleRemover} 
                                    historico={handleHistorico}
                                    score={handleScore}
                                />
                            ))
                        }
                    </List>
                    <Pagination 
                        page={page} 
                        rowsPerPage={rowsPerPage} 
                        totalCount={totalCount} 
                        handlePage={(e) => setPage(e.selected)} 
                    />
                </Container>
            }
        </>
    )
}

export default ListaEvento